import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface NewGuideDialogProps {
  open: boolean;
  onApply: (axis: 'horizontal' | 'vertical', position: number) => void;
  onClose: () => void;
}

/** "View → New Guide…" — Photoshop-style dialog for placing a
 *  guide at an exact scene-space coordinate. Pairs with the
 *  drag-from-ruler workflow (which is the discoverable surface
 *  when you want approximate positioning).
 *
 *  Axis radio + numeric position. Position is unbounded
 *  (off-canvas placement is allowed — useful for setting up
 *  bleed / margin guides outside the visible image). Resets on
 *  each open so successive opens don't carry stale state. */
export function NewGuideDialog({ open, onApply, onClose }: NewGuideDialogProps) {
  const [axis, setAxis] = useState<'horizontal' | 'vertical'>('horizontal');
  const [position, setPosition] = useState<string>('0');

  useEffect(() => {
    if (open) {
      setAxis('horizontal');
      setPosition('0');
    }
  }, [open]);

  const submit = () => {
    const n = Number.parseFloat(position);
    if (!Number.isFinite(n)) return;
    onApply(axis, n);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>New Guide</DialogTitle>
          <DialogDescription className="sr-only">
            Place a guide at an exact scene-space coordinate. Pick the orientation and enter the
            position in pixels.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Orientation</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="guide-axis"
                value="horizontal"
                checked={axis === 'horizontal'}
                onChange={() => setAxis('horizontal')}
              />
              Horizontal
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="guide-axis"
                value="vertical"
                checked={axis === 'vertical'}
                onChange={() => setAxis('vertical')}
              />
              Vertical
            </label>
          </fieldset>
          <div className="grid grid-cols-3 items-center gap-2">
            <Label htmlFor="new-guide-position">Position (px)</Label>
            <Input
              id="new-guide-position"
              type="number"
              step={1}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              className="col-span-2"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit}>Add Guide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
